import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminNotices } from "@/api/adminNoticeApi" // 💡 주석 해제 및 import 완료
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Plus, Pin } from "lucide-react"

type AdminNotice = {
  noticeId: number
  title: string
  isPinned: boolean
  visibleYn: string
  viewCount: number
  createdAt: string
}

export default function AdminNoticePage() {
  const navigate = useNavigate()

  const [notices, setNotices] = useState<AdminNotice[]>([])
  const [loading, setLoading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState("")

  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const pageSize = 10

  const fetchNotices = async (targetPage = page) => {
    try {
      setLoading(true)
      // 💡 API 실제 호출 적용
      const response = await getAdminNotices({
        searchKeyword: searchKeyword || undefined,
        page: targetPage,
        size: pageSize,
      })
      const responseData = response.data || response
      setNotices(responseData.items || [])
      setTotalPages(responseData.totalPages || 0)
      setTotalElements(responseData.totalElements || 0)
      setPage(targetPage)
    } catch (error) {
      console.error(error)
      alert("공지사항 목록을 불러오지 못했습니다.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotices(0)
  }, [])

  const handleSearch = () => fetchNotices(0)
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage >= totalPages) return
    fetchNotices(newPage)
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-bold">공지사항 관리</h2>
            <p className="mt-2 text-sm text-slate-500">고객에게 안내할 공지사항을 등록하고 관리합니다.</p>
          </div>
          <Button
            className="rounded-xl bg-slate-900 gap-2 h-11 px-5"
            onClick={() => navigate('/admin/cs/notices/new')}
          >
            <Plus size={18} />
            공지사항 등록
          </Button>
        </div>

        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
          <CardContent className="p-5 flex gap-3">
            <Input
              placeholder="공지사항 제목 검색"
              className="rounded-xl flex-1"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button className="rounded-xl px-8" onClick={handleSearch}>검색</Button>
          </CardContent>
        </Card>

        <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm text-left">
                <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                  <th className="px-4 py-4 font-medium w-20 text-center">No.</th>
                  <th className="px-4 py-4 font-medium">제목</th>
                  <th className="px-4 py-4 font-medium text-center w-24">상태</th>
                  <th className="px-4 py-4 font-medium text-center w-24">조회수</th>
                  <th className="px-4 py-4 font-medium text-center w-32">등록일</th>
                </tr>
                </thead>
                <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">조회 중...</td></tr>
                ) : notices.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">등록된 공지사항이 없습니다.</td></tr>
                ) : (
                  notices.map((notice) => (
                    <tr
                      key={notice.noticeId}
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${notice.isPinned ? 'bg-amber-50/30' : ''}`}
                      onClick={() => navigate(`/admin/cs/notices/${notice.noticeId}`)}
                    >
                      <td className="px-4 py-4 text-center text-slate-500">
                        {notice.isPinned ? <Pin size={16} className="mx-auto text-amber-500" fill="currentColor"/> : notice.noticeId}
                      </td>
                      <td className="px-4 py-4 font-medium text-slate-900">{notice.title}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${notice.visibleYn === 'Y' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                            {notice.visibleYn === 'Y' ? '노출' : '숨김'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center text-slate-500">{notice.viewCount}</td>
                      <td className="px-4 py-4 text-center text-slate-500">{notice.createdAt}</td>
                    </tr>
                  ))
                )}
                </tbody>
              </table>
            </div>

            {/* 💡 페이징 네비게이션 UI 복구됨 */}
            {totalPages > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-white">
                <div className="text-sm text-slate-500">
                  총 <span className="font-semibold text-slate-900">{totalElements}</span>건의 공지사항 (페이지 {page + 1} / {totalPages})
                </div>

                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg h-9 px-3 text-xs"
                    disabled={page === 0 || loading}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    이전
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i).map((p) => {
                      if (
                        totalPages > 7 &&
                        Math.abs(p - page) > 2 &&
                        p !== 0 &&
                        p !== totalPages - 1
                      ) {
                        if (p === 1 || p === totalPages - 2) {
                          return (
                            <span key={p} className="px-1 text-xs text-slate-400">
                              ...
                            </span>
                          )
                        }
                        return null
                      }

                      return (
                        <Button
                          key={p}
                          variant={p === page ? "default" : "ghost"}
                          size="sm"
                          className={`h-8 w-8 rounded-lg p-0 text-xs font-medium ${
                            p === page
                              ? "bg-slate-900 text-white hover:bg-slate-800"
                              : "text-slate-600 hover:bg-slate-100"
                          }`}
                          onClick={() => handlePageChange(p)}
                          disabled={loading}
                        >
                          {p + 1}
                        </Button>
                      )
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg h-9 px-3 text-xs"
                    disabled={page >= totalPages - 1 || loading}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    다음
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}